<script>
import { fade } from "svelte/transition";
import { onMount } from "svelte";
import { VList } from "virtua/svelte";
import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { diff } from "deep-object-diff";
import tcLib from "@cclib/lib/timecode.js";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import * as m from "@app/paraglide/messages.js";

// State for comparison
let selectedGroupA = $state(0);
let selectedGroupB = $state(eventGroupState.length > 1 ? 1 : 0);
let showOnlyDifferences = $state(false);
let selectedRowIndex = $state(null);

// VList state
let listRef = $state(null);
let listHeight = $state(500);
let resizeObserver;
let compareContentElement;

// Height calculation for VList
function calculateListHeight() {
    if (!compareContentElement) return;
    const modalBody = document.querySelector('#CompareEventGroupsModal .modal-body');
    const headerHeight = document.querySelector('#CompareEventGroupsModal .compare-header')?.offsetHeight || 0;
    const columnHeadersHeight = document.querySelector('#CompareEventGroupsModal .column-headers')?.offsetHeight || 0;
    const footerHeight = document.querySelector('#CompareEventGroupsModal .compare-footer')?.offsetHeight || 0;
    
    if (modalBody) {
        listHeight = modalBody.clientHeight - headerHeight - columnHeadersHeight - footerHeight - 10;
    }
}

onMount(() => {
    compareContentElement = document.querySelector('#CompareEventGroupsModal .compare-content');
    
    // Small delay to ensure modal is fully rendered
    setTimeout(() => {
        calculateListHeight();
    }, 100);

    // Create a resize observer to recalculate height when container changes
    resizeObserver = new ResizeObserver(() => {
        calculateListHeight();
    });

    const modalBody = document.querySelector('#CompareEventGroupsModal .modal-body');
    if (modalBody) {
        resizeObserver.observe(modalBody);
    }

    window.addEventListener('resize', calculateListHeight);

    return () => {
        window.removeEventListener('resize', calculateListHeight);
        if (resizeObserver) {
            resizeObserver.disconnect();
        }
    };
});

// Computed values
let groupA = $derived(eventGroupState[selectedGroupA]);
let groupB = $derived(eventGroupState[selectedGroupB]);

// Helper function to calculate similarity between two events
function calculateEventSimilarity(eventA, eventB) {
    if (!eventA || !eventB) return 0;
    
    let score = 0;
    let maxScore = 0;
    
    // Text similarity (most important - 50% weight)
    const textA = (eventA.text || '').replace(/<[^>]*>/g, '').toLowerCase().trim();
    const textB = (eventB.text || '').replace(/<[^>]*>/g, '').toLowerCase().trim();
    
    if (textA && textB) {
        // Simple text similarity using common words
        const wordsA = textA.split(/\s+/);
        const wordsB = textB.split(/\s+/);
        const commonWords = wordsA.filter(word => wordsB.includes(word)).length;
        const totalWords = Math.max(wordsA.length, wordsB.length);
        
        if (totalWords > 0) {
            score += (commonWords / totalWords) * 50;
        }
    }
    maxScore += 50;
    
    // Time overlap similarity (30% weight)
    if (typeof eventA.start === 'number' && typeof eventA.end === 'number' && 
        typeof eventB.start === 'number' && typeof eventB.end === 'number') {
        
        const overlapStart = Math.max(eventA.start, eventB.start);
        const overlapEnd = Math.min(eventA.end, eventB.end);
        const overlap = Math.max(0, overlapEnd - overlapStart);
        
        const durationA = eventA.end - eventA.start;
        const durationB = eventB.end - eventB.start;
        const maxDuration = Math.max(durationA, durationB);
        
        if (maxDuration > 0) {
            score += (overlap / maxDuration) * 30;
        }
    }
    maxScore += 30;
    
    // Start time proximity (20% weight)
    if (typeof eventA.start === 'number' && typeof eventB.start === 'number') {
        const timeDiff = Math.abs(eventA.start - eventB.start);
        const proximityScore = Math.max(0, 1 - (timeDiff / 10)); // 10 second window
        score += proximityScore * 20;
    }
    maxScore += 20;
    
    return maxScore > 0 ? score / maxScore : 0;
}

// Function to align events using dynamic programming approach
function alignEvents(eventsA, eventsB) {
    if (!eventsA?.length && !eventsB?.length) return [];
    if (!eventsA?.length) return eventsB.map((eventB, i) => ({ eventA: null, eventB, type: 'added', indexA: null, indexB: i }));
    if (!eventsB?.length) return eventsA.map((eventA, i) => ({ eventA, eventB: null, type: 'deleted', indexA: i, indexB: null }));
    
    const lenA = eventsA.length;
    const lenB = eventsB.length;
    
    // Dynamic programming matrix for alignment
    const dp = Array(lenA + 1).fill().map(() => Array(lenB + 1).fill(0));
    const trace = Array(lenA + 1).fill().map(() => Array(lenB + 1).fill(''));
    
    // Fill the DP table
    for (let i = 1; i <= lenA; i++) {
        for (let j = 1; j <= lenB; j++) {
            const similarity = calculateEventSimilarity(eventsA[i-1], eventsB[j-1]);
            
            // Match/substitution
            const matchScore = dp[i-1][j-1] + similarity;
            
            // Deletion (remove from A)
            const deleteScore = dp[i-1][j] - 0.1;
            
            // Insertion (add to A)
            const insertScore = dp[i][j-1] - 0.1;
            
            if (matchScore >= deleteScore && matchScore >= insertScore) {
                dp[i][j] = matchScore;
                trace[i][j] = 'match';
            } else if (deleteScore >= insertScore) {
                dp[i][j] = deleteScore;
                trace[i][j] = 'delete';
            } else {
                dp[i][j] = insertScore;
                trace[i][j] = 'insert';
            }
        }
    }
    
    // Backtrack to find alignment
    const alignment = [];
    let i = lenA, j = lenB;
    
    while (i > 0 || j > 0) {
        if (i > 0 && j > 0 && trace[i][j] === 'match') {
            const similarity = calculateEventSimilarity(eventsA[i-1], eventsB[j-1]);
            alignment.unshift({
                eventA: eventsA[i-1],
                eventB: eventsB[j-1],
                type: similarity > 0.3 ? 'matched' : 'modified',
                indexA: i-1,
                indexB: j-1,
                similarity
            });
            i--; j--;
        } else if (i > 0 && (j === 0 || trace[i][j] === 'delete')) {
            alignment.unshift({
                eventA: eventsA[i-1],
                eventB: null,
                type: 'deleted',
                indexA: i-1,
                indexB: null
            });
            i--;
        } else {
            alignment.unshift({
                eventA: null,
                eventB: eventsB[j-1],
                type: 'added',
                indexA: null,
                indexB: j-1
            });
            j--;
        }
    }
    
    return alignment;
}

// Helper function to format time differences as SMPTE timecode
function formatTimeDifference(oldValue, newValue, changeKey) {
    if ((changeKey === 'start' || changeKey === 'end') && typeof oldValue === 'number' && typeof newValue === 'number') {
        const oldTc = tcLib.secToTc(oldValue);
        const newTc = tcLib.secToTc(newValue);
        const diff = newValue - oldValue;
        const diffTc = tcLib.secToTc(Math.abs(diff));
        const direction = diff > 0 ? '+' : '-';
        
        return {
            type: 'timecode',
            oldTc,
            newTc,
            diffTc,
            direction,
            seconds: diff
        };
    }
    return null;
}

// Helper function to format change display text
function formatChangeDisplay(changeKey, changeValue, eventA, eventB) {
    const oldValue = eventA[changeKey];
    const newValue = eventB[changeKey];
    
    // Special handling for timecode changes
    const timeDiff = formatTimeDifference(oldValue, newValue, changeKey);
    if (timeDiff) {
        return {
            type: 'timecode',
            display: `${timeDiff.oldTc} → ${timeDiff.newTc} (${timeDiff.direction}${timeDiff.diffTc})`,
            class: 'timecode-change'
        };
    }
    
    // Special handling for badge-style arrays (speakers, recipients, tags)
    if (['speakers', 'recipients', 'tags'].includes(changeKey)) {
        const oldArray = Array.isArray(oldValue) ? oldValue : [];
        const newArray = Array.isArray(newValue) ? newValue : [];
        
        return {
            type: 'badges',
            oldArray,
            newArray,
            class: 'badge-change'
        };
    }
    
    // Special handling for voice property (object with name)
    if (changeKey === 'voice') {
        const oldVoice = oldValue?.name || m.compare_event_groups_none_label();
        const newVoice = newValue?.name || m.compare_event_groups_none_label();
        
        return {
            type: 'voice',
            display: `${oldVoice} → ${newVoice}`,
            class: 'voice-change'
        };
    }
    
    // Special handling for text changes (truncate if too long)
    if (changeKey === 'text') {
        const oldText = (oldValue || '').replace(/<[^>]*>/g, '').substring(0, 30);
        const newText = (newValue || '').replace(/<[^>]*>/g, '').substring(0, 30);
        return {
            type: 'text',
            display: `"${oldText}${oldText.length > 30 ? '...' : ''}" → "${newText}${newText.length > 30 ? '...' : ''}"`,
            class: 'text-change'
        };
    }
    
    // Default handling for other changes
    if (typeof changeValue === 'object') {
        return {
            type: 'object',
            display: JSON.stringify(changeValue),
            class: 'object-change'
        };
    }
    
    return {
        type: 'simple',
        display: `${oldValue} → ${newValue}`,
        class: 'simple-change'
    };
}

// Properties to compare - only relevant ones for comparison
const compareProperties = [
    'start', 'end', 'rate', 'xPos', 'yPos', 'writingMode', 'alignment', 
    'text', 'style', 'tags', 'speakers', 'recipients', 'voice', 
    'speakingStyle', 'voiceStability', 'voiceSimilarity', 'notes', 
    'reply', 'notesCheck', 'replyCheck', 'approved', 'forced'
];

// Function to extract only the properties we want to compare
function extractComparableProperties(event) {
    if (!event) return null;
    
    const comparable = {};
    compareProperties.forEach(prop => {
        if (event.hasOwnProperty(prop)) {
            // Special handling for boolean properties that might be undefined
            if ((prop === 'approved' || prop === 'forced') && event[prop] === undefined) {
                // Skip undefined boolean properties to avoid false differences
                return;
            }
            comparable[prop] = event[prop];
        }
    });
    return comparable;
}

// Calculate aligned events for the entire comparison
let alignedEvents = $derived.by(() => {
    if (!groupA || !groupB) return [];
    return alignEvents(groupA.events, groupB.events);
});

// Helper function to calculate difference for an aligned event (used inline in VList)
function calculateDifferenceForAligned(aligned, index) {
    if (!aligned) return null;
    
    if (aligned.type === 'matched') {
        const comparableA = extractComparableProperties(aligned.eventA);
        const comparableB = extractComparableProperties(aligned.eventB);
        const diffResult = diff(comparableA, comparableB);
        
        if (Object.keys(diffResult).length > 0) {
            return {
                index,
                type: 'modified',
                changes: diffResult,
                eventA: aligned.eventA,
                eventB: aligned.eventB,
                similarity: aligned.similarity
            };
        }
        return null;
    } else if (aligned.type === 'modified') {
        const comparableA = extractComparableProperties(aligned.eventA);
        const comparableB = extractComparableProperties(aligned.eventB);
        const diffResult = diff(comparableA, comparableB);
        
        return {
            index,
            type: 'modified',
            changes: diffResult,
            eventA: aligned.eventA,
            eventB: aligned.eventB,
            similarity: aligned.similarity
        };
    } else if (aligned.type === 'deleted') {
        return {
            index,
            type: 'deleted',
            eventA: aligned.eventA,
            eventB: null
        };
    } else if (aligned.type === 'added') {
        return {
            index,
            type: 'added',
            eventA: null,
            eventB: aligned.eventB
        };
    }
    return null;
}

// Filter aligned events based on showOnlyDifferences toggle
let filteredAlignedEvents = $derived.by(() => {
    if (!showOnlyDifferences) return alignedEvents;
    
    return alignedEvents.filter(aligned => {
        if (aligned.type === 'added' || aligned.type === 'deleted') {
            return true; // Always show additions and deletions
        }
        
        if (aligned.type === 'matched' || aligned.type === 'modified') {
            // Check if events have actual differences
            const comparableA = extractComparableProperties(aligned.eventA);
            const comparableB = extractComparableProperties(aligned.eventB);
            const diffResult = diff(comparableA, comparableB);
            
            return Object.keys(diffResult).length > 0;
        }
        
        return false;
    });
});

// Find next/previous differences based on filtered aligned events
let allDifferences = $derived.by(() => {
    if (!filteredAlignedEvents.length) return [];
    
    // If showing only differences, every filtered event is a difference
    if (showOnlyDifferences) {
        return Array.from({ length: filteredAlignedEvents.length }, (_, i) => i);
    }
    
    // Otherwise, find which events have actual differences
    const differences = [];
    
    for (let i = 0; i < filteredAlignedEvents.length; i++) {
        const aligned = filteredAlignedEvents[i];
        
        if (aligned.type === 'added' || aligned.type === 'deleted') {
            differences.push(i);
        } else if (aligned.type === 'matched' || aligned.type === 'modified') {
            // Check if events have actual differences
            const comparableA = extractComparableProperties(aligned.eventA);
            const comparableB = extractComparableProperties(aligned.eventB);
            const diffResult = diff(comparableA, comparableB);
            
            if (Object.keys(diffResult).length > 0) {
                differences.push(i);
            }
        }
    }
    return differences;
});

let nextDifferenceIndex = $derived.by(() => {
    if (!allDifferences.length) return undefined;
    const currentIndex = selectedRowIndex !== null ? selectedRowIndex : -1;
    return allDifferences.find(index => index > currentIndex);
});

let previousDifferenceIndex = $derived.by(() => {
    if (!allDifferences.length) return undefined;
    const currentIndex = selectedRowIndex !== null ? selectedRowIndex : allDifferences[allDifferences.length - 1] + 1;
    return [...allDifferences].reverse().find(index => index < currentIndex);
});

// Navigation functions
function goToNextDifference() {
    if (nextDifferenceIndex !== undefined && listRef) {
        selectedRowIndex = nextDifferenceIndex;
        listRef.scrollToIndex(nextDifferenceIndex, {
            align: "center",
            smooth: true
        });
    }
}

function goToPreviousDifference() {
    if (previousDifferenceIndex !== undefined && listRef) {
        selectedRowIndex = previousDifferenceIndex;
        listRef.scrollToIndex(previousDifferenceIndex, {
            align: "center",
            smooth: true
        });
    }
}

// Row selection handler
function selectRow(index) {
    selectedRowIndex = selectedRowIndex === index ? null : index;
    
    // Scroll to selected row using VList
    if (selectedRowIndex !== null && listRef) {
        listRef.scrollToIndex(selectedRowIndex, {
            align: "center",
            smooth: true
        });
    }
}

function getDifferenceLabel(type) {
    switch (type) {
        case 'modified':
            return m.compare_event_groups_diff_type_modified();
        case 'added':
            return m.compare_event_groups_diff_type_added();
        case 'deleted':
            return m.compare_event_groups_diff_type_deleted();
        default:
            return type;
    }
}

// Helper function to generate a new unique ID for an event
function generateNewEventId() {
    return crypto.randomUUID();
}

// Apply changes functions
function applyChangeAtoB(indexA, indexB, alignedEventA, alignedEventB) {
    if (alignedEventA) {
        // Create a deep copy and assign a new unique ID
        const newEvent = JSON.parse(JSON.stringify(alignedEventA));
        newEvent.id = generateNewEventId();
        
        if (indexB !== null && indexB < eventGroupState[selectedGroupB].events.length) {
            // Replace existing event in B (keep the new ID for consistency)
            eventGroupState[selectedGroupB].events[indexB] = newEvent;
        } else {
            // Event doesn't exist in B - find the best insertion position
            // Use the position relative to surrounding events or append at end
            let insertPosition = eventGroupState[selectedGroupB].events.length;
            
            // Try to find a good insertion point based on time
            if (alignedEventA.start !== undefined) {
                for (let i = 0; i < eventGroupState[selectedGroupB].events.length; i++) {
                    if (eventGroupState[selectedGroupB].events[i].start > alignedEventA.start) {
                        insertPosition = i;
                        break;
                    }
                }
            }
            
            eventGroupState[selectedGroupB].events.splice(insertPosition, 0, newEvent);
        }
        
        insertHistoryAction({
            name: "apply change A to B",
            eventGroup: selectedGroupB,
            snapshots: [{
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            }],
        });
        
        toast.push(m.compare_event_groups_change_applied_a_to_b(), { classes: ["toast-success"] });
    }
}

function applyChangeBtoA(indexA, indexB, alignedEventA, alignedEventB) {
    if (alignedEventB) {
        // Create a deep copy and assign a new unique ID
        const newEvent = JSON.parse(JSON.stringify(alignedEventB));
        newEvent.id = generateNewEventId();
        
        if (indexA !== null && indexA < eventGroupState[selectedGroupA].events.length) {
            // Replace existing event in A (keep the new ID for consistency)
            eventGroupState[selectedGroupA].events[indexA] = newEvent;
        } else {
            // Event doesn't exist in A - find the best insertion position
            // Use the position relative to surrounding events or append at end
            let insertPosition = eventGroupState[selectedGroupA].events.length;
            
            // Try to find a good insertion point based on time
            if (alignedEventB.start !== undefined) {
                for (let i = 0; i < eventGroupState[selectedGroupA].events.length; i++) {
                    if (eventGroupState[selectedGroupA].events[i].start > alignedEventB.start) {
                        insertPosition = i;
                        break;
                    }
                }
            }
            
            eventGroupState[selectedGroupA].events.splice(insertPosition, 0, newEvent);
        }
        
        insertHistoryAction({
            name: "apply change B to A",
            eventGroup: selectedGroupA,
            snapshots: [{
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            }],
        });
        
        toast.push(m.compare_event_groups_change_applied_b_to_a(), { classes: ["toast-success"] });
    }
}

// CSV Export function
function exportDifferencesToCSV() {
    if (!groupA || !groupB) return;
    
    const csvData = [];
    const maxLength = Math.max(groupA.events.length, groupB.events.length);
    
    for (let i = 0; i < maxLength; i++) {
        const eventA = groupA.events[i];
        const eventB = groupB.events[i];
        
        let changeType = 'no-change';
        let changeTypeLabel = '';
        let changes = '';
        
        if (eventA && eventB) {
            // Extract only comparable properties
            const comparableA = extractComparableProperties(eventA);
            const comparableB = extractComparableProperties(eventB);
            const diffResult = diff(comparableA, comparableB);
            
            if (Object.keys(diffResult).length > 0) {
                changeType = 'modified';
                changeTypeLabel = m.compare_event_groups_csv_change_type_modified();
                changes = JSON.stringify(diffResult, null, 2);
            }
        } else if (eventA && !eventB) {
            changeType = 'deleted';
            changeTypeLabel = m.compare_event_groups_csv_change_type_deleted();
            changes = m.compare_event_groups_csv_deleted_description();
        } else if (!eventA && eventB) {
            changeType = 'added';
            changeTypeLabel = m.compare_event_groups_csv_change_type_added();
            changes = m.compare_event_groups_csv_added_description();
        }
        
        if (changeType !== 'no-change') {
            csvData.push({
                [m.compare_event_groups_csv_event_index_header()]: i + 1,
                [m.compare_event_groups_csv_change_type_header()]: changeTypeLabel,
                [m.compare_event_groups_csv_group_a_text_header()]: eventA?.text || '',
                [m.compare_event_groups_csv_group_a_start_header()]: eventA?.start || '',
                [m.compare_event_groups_csv_group_a_end_header()]: eventA?.end || '',
                [m.compare_event_groups_csv_group_b_text_header()]: eventB?.text || '',
                [m.compare_event_groups_csv_group_b_start_header()]: eventB?.start || '',
                [m.compare_event_groups_csv_group_b_end_header()]: eventB?.end || '',
                [m.compare_event_groups_csv_changes_header()]: changes
            });
        }
    }
    
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const filename = `event-group-comparison-${groupA.name}-vs-${groupB.name}-${new Date().toISOString().split('T')[0]}.csv`;
    saveAs(blob, filename);
    
    toast.push(m.compare_event_groups_exported_csv_toast(), { classes: ["toast-success"] });
}
</script>

<div 
    transition:fade={{ duration: 100 }} 
    class="modal {modalState.selected === 'compareEventGroups' ? 'show d-block' : ''}" 
    role="dialog" 
    aria-labelledby="compareTitle"
    aria-modal="true"
    tabindex="-1" 
    id="CompareEventGroupsModal">
    <div class="modal-dialog modal-fullscreen">
        <div class="modal-content">
            <div class="modal-header">
                <h5 class="modal-title" id="compareTitle">
                    <i class="bi bi-arrow-left-right me-2"></i>
                    {m.compare_event_groups_title()}
                </h5>
                <button type="button" class="btn-close" onclick={hideModal} aria-label={m.compare_event_groups_close_label()}></button>
            </div>
            
            <div class="modal-body p-0 d-flex flex-column">
                <!-- Header controls -->
                <div class="compare-header p-3 border-bottom flex-shrink-0">
                    <div class="row align-items-center">
                        <div class="col-3">
                            <label class="form-label" for="groupASelect">{m.compare_event_groups_event_group_a_label()}</label>
                            <select class="form-select" id="groupASelect" bind:value={selectedGroupA}>
                                {#each eventGroupState as group, index (group.id ?? index)}
                                    <option value={index}>{group.name} ({group.type})</option>
                                {/each}
                            </select>
                        </div>
                        
                        <div class="col-6 text-center">
                            <div class="btn-group" role="group">
                                <button 
                                    type="button" 
                                    class="btn btn-outline-primary"
                                    onclick={() => goToPreviousDifference()}
                                    disabled={previousDifferenceIndex === undefined}>
                                    <i class="bi bi-arrow-left me-1"></i>
                                    {m.compare_event_groups_previous_difference()}
                                </button>
                                <button 
                                    type="button" 
                                    class="btn btn-outline-primary"
                                    onclick={() => goToNextDifference()}
                                    disabled={nextDifferenceIndex === undefined}>
                                    {m.compare_event_groups_next_difference()}
                                    <i class="bi bi-arrow-right ms-1"></i>
                                </button>
                            </div>
                            
                            <div class="mt-2">
                                <button 
                                    type="button" 
                                    class="btn btn-success btn-sm me-2"
                                    onclick={exportDifferencesToCSV}>
                                    <i class="bi bi-download me-1"></i>
                                    {m.compare_event_groups_export_csv()}
                                </button>
                                
                                <div class="form-check form-check-inline">
                                    <input 
                                        class="form-check-input" 
                                        type="checkbox" 
                                        id="showDifferencesOnly" 
                                        bind:checked={showOnlyDifferences}>
                                    <label class="form-check-label" for="showDifferencesOnly">
                                        {m.compare_event_groups_show_differences_only()}
                                    </label>
                                </div>
                            </div>
                        </div>
                        
                        <div class="col-3">
                            <label class="form-label" for="groupBSelect">{m.compare_event_groups_event_group_b_label()}</label>
                            <select class="form-select" id="groupBSelect" bind:value={selectedGroupB}>
                                {#each eventGroupState as group, index (group.id ?? index)}
                                    <option value={index}>{group.name} ({group.type})</option>
                                {/each}
                            </select>
                        </div>
                    </div>
                </div>
                
                <!-- Comparison content -->
                <div class="compare-content flex-grow-1" bind:this={compareContentElement}>
                    <!-- Column Headers -->
                    <div class="column-headers">
                        <div class="row g-0">
                            <div class="col-4 border-end">
                                <div class="column-header p-2 bg-primary text-white">
                                    <h6 class="mb-0">
                                        <i class="bi bi-file-text me-1"></i>
                                        {m.compare_event_groups_group_events_label({ name: groupA?.name || m.compare_event_groups_group_a_fallback() })}
                                    </h6>
                                </div>
                            </div>
                            <div class="col-4 border-end">
                                <div class="column-header p-2 bg-warning text-dark">
                                    <h6 class="mb-0">
                                        <i class="bi bi-arrows-expand me-1"></i>
                                        {m.compare_event_groups_differences_actions_heading()}
                                    </h6>
                                </div>
                            </div>
                            <div class="col-4">
                                <div class="column-header p-2 bg-primary text-white">
                                    <h6 class="mb-0">
                                        <i class="bi bi-file-text me-1"></i>
                                        {m.compare_event_groups_group_events_label({ name: groupB?.name || m.compare_event_groups_group_b_fallback() })}
                                    </h6>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Virtual List for synchronized rows -->
                    <VList 
                        bind:this={listRef}
                        style="height:{listHeight}px;"
                        data={filteredAlignedEvents}
                        getKey={(item, index) => `${item.indexA}-${item.indexB}-${index}`}
                        itemSize={229}
                    >
                        {#snippet children(aligned, alignedIndex)}
                            {@const eventA = aligned?.eventA}
                            {@const eventB = aligned?.eventB}
                            {@const difference = calculateDifferenceForAligned(aligned, alignedIndex)}
                            
                            <div class="synchronized-row row g-0 {selectedRowIndex === alignedIndex ? 'selected-row' : ''}" 
                                 data-event-index={alignedIndex}
                                 onclick={() => selectRow(alignedIndex)}
                                 onkeydown={(e) => e.key === 'Enter' || e.key === ' ' ? selectRow(alignedIndex) : null}
                                 role="button"
                                 tabindex="0">
                                <!-- Group A Column -->
                                <div class="col-4 border-end disabled-interaction">
                                    <div class="event-container">
                                        {#if eventA}
                                            <div class="event-wrapper">
                                                <div class="simple-event-display p-3">
                                                    <div class="event-number fw-bold text-muted mb-2">
                                                        A{aligned.indexA !== null ? aligned.indexA + 1 : '?'}
                                                        {#if aligned.similarity !== undefined}
                                                            <span class="similarity-badge badge badge-secondary ms-1" title={m.compare_event_groups_similarity_title({ percent: Math.round(aligned.similarity * 100) })}>
                                                                {Math.round(aligned.similarity * 100)}%
                                                            </span>
                                                        {/if}
                                                    </div>
                                                    <div class="event-timecode mb-2">
                                                        <div class="small text-muted">{m.compare_event_groups_start_label()} <span class="text-dark">{tcLib.secToTc(eventA.start, projectState.frameRate, projectState.dropFrame) || m.compare_event_groups_not_set()}</span></div>
                                                        <div class="small text-muted">{m.compare_event_groups_end_label()} <span class="text-dark">{tcLib.secToTc(eventA.end, projectState.frameRate, projectState.dropFrame) || m.compare_event_groups_not_set()}</span></div>
                                                    </div>
                                                    <div class="event-text">
                                                        <div class="text-content EditorPreviewText text-center">{@html eventA.text || `<em class="text-muted">${m.compare_event_groups_no_text()}</em>`}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        {:else}
                                            <div class="event-wrapper empty-event">
                                                <div class="p-3 text-muted text-center">
                                                    <i class="bi bi-dash-circle"></i>
                                                    {m.compare_event_groups_no_event_deleted()}
                                                </div>
                                            </div>
                                        {/if}
                                    </div>
                                </div>
                        
                                <!-- Differences Column -->
                                <div class="col-4 border-end">
                                    <div class="difference-container">
                                        <div class="difference-item">
                                            {#if difference}
                                                <div class="difference-header p-2 bg-light">
                                                    <small class="text-muted">{m.compare_event_groups_alignment_label({ index: alignedIndex + 1 })}</small>
                                                    <span class="badge bg-{difference.type === 'modified' ? 'warning' : difference.type === 'added' ? 'success' : 'danger'} ms-2">
                                                        {getDifferenceLabel(difference.type)}
                                                    </span>
                                                </div>
                                                
                                                <div class="difference-content">
                                                    {#if difference.type === 'modified'}
                                                        <div class="p-2">
                                                            <div class="changes-summary mb-2">
                                                                {#each Object.keys(difference.changes) as changeKey (changeKey)}
                                                                    {@const changeDisplay = formatChangeDisplay(changeKey, difference.changes[changeKey], difference.eventA, difference.eventB)}
                                                                    <div class="change-item change-item-compact">
                                                                        <div class="change-label">{changeKey}:</div>
                                                                        <div class="change-value {changeDisplay.class}">
                                                                            {#if changeDisplay.type === 'badges'}
                                                                                <!-- Before badges -->
                                                                                <div class="badge-comparison">
                                                                                    <div class="badge-group">
                                                                                        <span class="badge-label">{m.compare_event_groups_before_label()}</span>
                                                                                        {#each changeDisplay.oldArray as item (item.id ?? item.name ?? item)}
                                                                                            <span class="badge badge-sm me-1 {item.colour === 'yellow' ? 'text-dark' : 'text-white'}" 
                                                                                                  style="background-color: {item.colour || '#6c757d'};">
                                                                                                {item.name || item}
                                                                                            </span>
                                                                                        {:else}
                                                                                            <span class="text-muted small">{m.compare_event_groups_none_label()}</span>
                                                                                        {/each}
                                                                                    </div>
                                                                                    <div class="badge-group mt-1">
                                                                                        <span class="badge-label">{m.compare_event_groups_after_label()}</span>
                                                                                        {#each changeDisplay.newArray as item (item.id ?? item.name ?? item)}
                                                                                            <span class="badge badge-sm me-1 {item.colour === 'yellow' ? 'text-dark' : 'text-white'}" 
                                                                                                  style="background-color: {item.colour || '#6c757d'};">
                                                                                                {item.name || item}
                                                                                            </span>
                                                                                        {:else}
                                                                                            <span class="text-muted small">{m.compare_event_groups_none_label()}</span>
                                                                                        {/each}
                                                                                    </div>
                                                                                </div>
                                                                            {:else}
                                                                                {changeDisplay.display}
                                                                            {/if}
                                                                        </div>
                                                                    </div>
                                                                {/each}
                                                            </div>
                                                            
                                                            <div class="action-buttons">
                                                                <button 
                                                                    type="button" 
                                                                    class="btn btn-sm btn-outline-primary me-1"
                                                                    onclick={() => applyChangeAtoB(aligned.indexA, aligned.indexB, aligned.eventA, aligned.eventB)}
                                                                    title={m.compare_event_groups_apply_a_to_b_title()}>
                                                                    A → B
                                                                </button>
                                                                <button 
                                                                    type="button" 
                                                                    class="btn btn-sm btn-outline-primary"
                                                                    onclick={() => applyChangeBtoA(aligned.indexA, aligned.indexB, aligned.eventA, aligned.eventB)}
                                                                    title={m.compare_event_groups_apply_b_to_a_title()}>
                                                                    B → A
                                                                </button>
                                                            </div>
                                                        </div>
                                                    {:else if difference.type === 'added'}
                                                        <div class="p-2">
                                                            <p class="small text-muted">{m.compare_event_groups_added_description()}</p>
                                                            <button 
                                                                type="button" 
                                                                class="btn btn-sm btn-outline-success"
                                                                onclick={() => applyChangeBtoA(aligned.indexA, aligned.indexB, aligned.eventA, aligned.eventB)}
                                                                title={m.compare_event_groups_copy_event_b_to_a_title()}>
                                                                {m.compare_event_groups_copy_to_a()}
                                                            </button>
                                                        </div>
                                                    {:else if difference.type === 'deleted'}
                                                        <div class="p-2">
                                                            <p class="small text-muted">{m.compare_event_groups_deleted_description()}</p>
                                                            <button 
                                                                type="button" 
                                                                class="btn btn-sm btn-outline-danger"
                                                                onclick={() => applyChangeAtoB(aligned.indexA, aligned.indexB, aligned.eventA, aligned.eventB)}
                                                                title={m.compare_event_groups_copy_event_a_to_b_title()}>
                                                                {m.compare_event_groups_copy_to_b()}
                                                            </button>
                                                        </div>
                                                    {/if}
                                                </div>
                                            {:else}
                                                <div class="no-difference d-flex flex-column align-items-center justify-content-center h-100">
                                                    <i class="bi bi-check-circle-fill text-success"></i>
                                                    <div class="small mt-1">{m.compare_event_groups_no_differences()}</div>
                                                </div>
                                            {/if}
                                        </div>
                                    </div>
                                </div>
                        
                                <!-- Group B Column -->
                                <div class="col-4 disabled-interaction">
                                    <div class="event-container">
                                        {#if eventB}
                                            <div class="event-wrapper">
                                                <div class="simple-event-display p-3">
                                                    <div class="event-number fw-bold text-muted mb-2">
                                                        B{aligned.indexB !== null ? aligned.indexB + 1 : '?'}
                                                        {#if aligned.similarity !== undefined}
                                                            <span class="similarity-badge badge badge-secondary ms-1" title={m.compare_event_groups_similarity_title({ percent: Math.round(aligned.similarity * 100) })}>
                                                                {Math.round(aligned.similarity * 100)}%
                                                            </span>
                                                        {/if}
                                                    </div>
                                                    <div class="event-timecode mb-2">
                                                        <div class="small text-muted">{m.compare_event_groups_start_label()} <span class="text-dark">{tcLib.secToTc(eventB.start, projectState.frameRate, projectState.dropFrame) || m.compare_event_groups_not_set()}</span></div>
                                                        <div class="small text-muted">{m.compare_event_groups_end_label()} <span class="text-dark">{tcLib.secToTc(eventB.end, projectState.frameRate, projectState.dropFrame) || m.compare_event_groups_not_set()}</span></div>
                                                    </div>
                                                    <div class="event-text">
                                                        <div class="text-content EditorPreviewText text-center">{@html eventB.text || `<em class="text-muted">${m.compare_event_groups_no_text()}</em>`}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        {:else}
                                            <div class="event-wrapper empty-event">
                                                <div class="p-3 text-muted text-center">
                                                    <i class="bi bi-dash-circle"></i>
                                                    {m.compare_event_groups_no_event_added()}
                                                </div>
                                            </div>
                                        {/if}
                                    </div>
                                </div>
                            </div>
                        {/snippet}
                    </VList>
                </div>
                
                <!-- Summary footer -->
                <div class="compare-footer p-3 border-top bg-light flex-shrink-0">
                    <div class="d-flex justify-content-center">
                        <div class="pagination-info">
                            {m.compare_event_groups_summary({ aligned: filteredAlignedEvents.length, differences: allDifferences.length })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

<style>
.selected-row {
    background-color: rgba(13, 110, 253, 0.1) !important;
    border-left: 3px solid #0d6efd !important;
    transition: all 0.2s ease;
}

.synchronized-row {
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.synchronized-row:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

.modal-fullscreen .modal-content {
    height: 100vh;
    border-radius: 0;
    display: flex;
    flex-direction: column;
}

.modal-body {
    flex: 1;
    overflow: hidden;
}

.compare-content {
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.column-headers {
    position: sticky;
    top: 0;
    z-index: 10;
    background: white;
    border-bottom: 1px solid var(--bs-border-color);
    flex-shrink: 0;
}

.synchronized-row {
    min-height: 229px;
    border-bottom: 1px solid var(--bs-border-color);
}

.event-container,
.difference-container {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.event-wrapper {
    height: 229px;
    min-height: 229px;
    max-height: 229px;
}

.empty-event {
    height: 229px;
    min-height: 229px;
    max-height: 229px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--bs-light);
}

.difference-item {
    height: 229px;
    min-height: 229px;
    max-height: 229px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.difference-content {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
}

.no-difference {
    height: 229px;
    min-height: 229px;
    max-height: 229px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.changes-summary {
    max-height: 120px;
    overflow-y: auto;
    font-size: 0.85rem;
}

.change-item {
    margin-bottom: 0.5rem;
    padding: 0.25rem;
    background-color: var(--bs-light);
    border-radius: 0.25rem;
}

.change-item-compact {
    margin-bottom: 0.25rem;
    padding: 0.125rem 0.25rem;
    background-color: transparent;
    border-left: 3px solid var(--bs-primary);
    font-size: 0.75rem;
    line-height: 1.2;
}

.change-label {
    font-weight: 600;
    color: var(--bs-dark);
    margin-bottom: 0.125rem;
}

.change-value {
    font-family: 'Courier New', monospace;
    font-size: 0.7rem;
    word-break: break-all;
}

.timecode-change {
    color: var(--bs-primary);
    font-weight: 500;
}

.text-change {
    color: var(--bs-info);
    font-style: italic;
}

.object-change {
    color: var(--bs-warning);
    max-height: 40px;
    overflow: hidden;
}

.simple-change {
    color: var(--bs-secondary);
}

.voice-change {
    color: var(--bs-success);
    font-weight: 500;
}

.badge-change {
    font-family: inherit;
}

.badge-comparison {
    font-size: 0.65rem;
}

.badge-group {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.125rem;
}

.badge-label {
    font-weight: 600;
    color: var(--bs-dark);
    margin-right: 0.25rem;
    font-size: 0.6rem;
}

.badge-sm {
    font-size: 0.55rem;
    padding: 0.125rem 0.25rem;
}

.action-buttons {
    margin-top: auto;
    padding-top: 0.5rem;
}

.pagination-info {
    font-size: 0.9rem;
    color: var(--bs-secondary);
}

/* Simple event display styles */
.simple-event-display {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.simple-event-display .event-number {
    font-size: 0.875rem;
}

.simple-event-display .event-timecode {
    font-family: 'Courier New', monospace;
}

.simple-event-display .event-text {
    flex: 1;
    overflow-y: auto;
}

.simple-event-display .text-content {
    font-size: 0.9rem;
    line-height: 1.4;
    max-height: 140px;
    overflow-y: auto;
    padding: 0.5rem;
    background-color: var(--bs-light);
    border-radius: 0.25rem;
    border: 1px solid var(--bs-border-color);
}

.simple-event-display .text-content:empty::before {
    content: 'No text content';
    color: var(--bs-secondary);
    font-style: italic;
}

/* Ensure columns maintain equal height */
.row.g-0 {
    min-height: 100%;
}

.col-4 {
    display: flex;
    flex-direction: column;
}

/* Disable interactions for columns 1 and 3 (Event Groups A and B) */
.disabled-interaction {
    pointer-events: none;
    user-select: none;
    position: relative;
}

.disabled-interaction::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(0.5px);
    z-index: 5;
    pointer-events: none;
}

.disabled-interaction .event-wrapper {
    opacity: 0.85;
    filter: grayscale(0.1);
}

/* Add a subtle visual indicator */
.disabled-interaction .event-wrapper::before {
    content: '🔒';
    position: absolute;
    top: 5px;
    right: 5px;
    font-size: 12px;
    opacity: 0.6;
    z-index: 10;
    pointer-events: none;
}

/* Ensure the compare column remains fully interactive */
.difference-container {
    pointer-events: auto;
    user-select: auto;
}

/* Override for empty events to maintain visual consistency */
.disabled-interaction .empty-event {
    opacity: 0.7;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .compare-content {
        height: auto;
    }
    
    .column-headers .row {
        flex-direction: column;
    }
    
    .column-headers .col-4 {
        width: 100%;
        border-bottom: 1px solid var(--bs-border-color);
        border-right: none !important;
    }
    
    /* Maintain disabled state on mobile */
    .disabled-interaction {
        pointer-events: none;
    }
}
</style>